import * as React from "react";
import {
  FormProvider,
  FormFieldController,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
  Input,
  Button,
} from "@aisquare/ui";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Section, VariantBlock } from "../lib/Section";

// A real zod-validated mini form. The schema is the single source of truth —
// FormField supplies the focus / id / aria-describedby / aria-invalid wiring
// and routes errors through the `text-destructive` token, so the consumer
// writes a schema plus fields and gets the full a11y contract for free.
const signInSchema = z.object({
  email: z.string().min(1, "Email is required").email("Enter a valid email"),
});
type SignInValues = z.infer<typeof signInSchema>;

function SignInForm() {
  const [submitted, setSubmitted] = React.useState<string | null>(null);
  const form = useForm<SignInValues>({
    resolver: zodResolver(signInSchema),
    defaultValues: { email: "" },
    mode: "onSubmit",
  });

  return (
    <FormProvider {...form}>
      <form
        onSubmit={form.handleSubmit((values) => setSubmitted(values.email))}
        noValidate
        className="w-80 space-y-4"
      >
        <FormFieldController
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="you@example.com" {...field} />
              </FormControl>
              <FormDescription>We never share it.</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit">Sign in</Button>
        {submitted ? (
          <p className="text-sm text-muted-foreground">Submitted: {submitted}</p>
        ) : null}
      </form>
    </FormProvider>
  );
}

// Two fields in one provider — proves each FormItem mints its own id scheme,
// so labels, controls, and messages stay associated independently.
const profileSchema = z.object({
  displayName: z.string().min(2, "Use at least 2 characters"),
  handle: z
    .string()
    .min(1, "Pick a handle")
    .regex(/^[a-z0-9_]+$/, "Lowercase letters, numbers, and _ only"),
});
type ProfileValues = z.infer<typeof profileSchema>;

function ProfileForm() {
  const form = useForm<ProfileValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: { displayName: "", handle: "" },
    mode: "onSubmit",
  });

  return (
    <FormProvider {...form}>
      <form
        onSubmit={form.handleSubmit(() => {})}
        noValidate
        className="w-80 space-y-4"
      >
        <FormFieldController
          control={form.control}
          name="displayName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Display name</FormLabel>
              <FormControl>
                <Input placeholder="Ada Lovelace" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormFieldController
          control={form.control}
          name="handle"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Handle</FormLabel>
              <FormControl>
                <Input placeholder="ada_l" {...field} />
              </FormControl>
              <FormDescription>This is your public @handle.</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit">Save profile</Button>
      </form>
    </FormProvider>
  );
}

// The `tight` spacing variant on FormItem — `space-y-1` instead of the roomy
// `space-y-2` default — for dense rows (a settings hub) sharing one component.
const tightSchema = z.object({
  apiKey: z.string().min(8, "Keys are at least 8 characters"),
});
type TightValues = z.infer<typeof tightSchema>;

function TightForm() {
  const form = useForm<TightValues>({
    resolver: zodResolver(tightSchema),
    defaultValues: { apiKey: "" },
    mode: "onSubmit",
  });

  return (
    <FormProvider {...form}>
      <form
        onSubmit={form.handleSubmit(() => {})}
        noValidate
        className="w-80 space-y-4"
      >
        <FormFieldController
          control={form.control}
          name="apiKey"
          render={({ field }) => (
            <FormItem spacing="tight">
              <FormLabel>API key</FormLabel>
              <FormControl>
                <Input placeholder="sk-…" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit" size="sm">
          Validate
        </Button>
      </form>
    </FormProvider>
  );
}

export function FormFieldDemo() {
  return (
    <Section
      id="form-field"
      title="FormField"
      description="The canonical shadcn react-hook-form + zod wrapper, namespaced and brand-themed. FormProvider (donor Form) + FormFieldController (donor FormField) plus FormItem / FormLabel / FormControl / FormDescription / FormMessage collapse a field's id, label htmlFor, aria-describedby, and aria-invalid plumbing into one anatomy. Errors animate in and out (reduced-motion aware) through the text-destructive token, and the wrapped control's border morphs idle to error to valid via aria-invalid."
    >
      <VariantBlock
        label="Validated field"
        caption="submit empty to see the required error, then type a valid email to clear it"
      >
        <SignInForm />
      </VariantBlock>

      <VariantBlock
        label="Multiple fields"
        caption="each FormItem owns its own id scheme, so the two fields validate independently"
      >
        <ProfileForm />
      </VariantBlock>

      <VariantBlock
        label="Tight spacing"
        caption="FormItem spacing=tight for dense rows"
      >
        <TightForm />
      </VariantBlock>
    </Section>
  );
}
